import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Building2,
  Shield,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  Radio,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

/**
 * T10 (Arco A / C4): envelope REAL da Edge check-fiscal-status (§I.142 Fase 4, motor
 * próprio ACBr) — { company, sefaz, recommendations }. A versão anterior esperava
 * certificate/distribution/sync_log da era Nuvem Fiscal e estourava TypeError.
 */
interface FiscalDiagnostic {
  success: boolean;
  error?: string;
  company?: {
    cnpj: string;
    razao_social: string;
    ambiente: 'producao' | 'homologacao';
    nfce_environment?: string | null;
  };
  sefaz?: {
    cStat: number | null;
    xMotivo: string | null;
    dhRecbto: string | null;
  } | null;
  recommendations?: string[];
}

interface FiscalDiagnosticDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diagnostic: FiscalDiagnostic | null;
  isLoading: boolean;
  onRefresh: () => void;
}

function StatusIcon({ status }: { status: 'ok' | 'warning' | 'error' | 'info' }) {
  switch (status) {
    case 'ok':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    case 'error':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'info':
      return <Info className="h-5 w-5 text-blue-500" />;
  }
}

function DiagnosticSection({
  icon: Icon,
  title,
  children
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="ml-7 space-y-2 text-sm">
        {children}
      </div>
    </div>
  );
}

function formatCnpj(cnpj: string): string {
  if (!cnpj || cnpj.length !== 14) return cnpj;
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function ambienteLabel(value: string | null | undefined): string {
  if (!value) return '-';
  return value === 'producao' ? '🟢 PRODUÇÃO' : '🟡 HOMOLOGAÇÃO';
}

export function FiscalDiagnosticDialog({
  open,
  onOpenChange,
  diagnostic,
  isLoading,
  onRefresh,
}: FiscalDiagnosticDialogProps) {
  // cStat 107 = "Serviço em Operação" (consulta de status do serviço SEFAZ)
  const getSefazStatus = (): 'ok' | 'warning' | 'error' => {
    if (!diagnostic?.sefaz) return 'error';
    if (diagnostic.sefaz.cStat === 107) return 'ok';
    return 'warning';
  };

  const recommendations = diagnostic?.recommendations ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Diagnóstico Fiscal
          </DialogTitle>
          <DialogDescription>
            Verificação do motor fiscal próprio e do serviço da SEFAZ
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Consultando motor fiscal...</p>
          </div>
        ) : diagnostic?.success === false ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <XCircle className="h-12 w-12 text-red-500" />
            <p className="text-center text-muted-foreground">{diagnostic.error || 'Erro ao executar diagnóstico'}</p>
            {recommendations.length > 0 && (
              <div className="w-full space-y-1 p-3 bg-muted/50 rounded-lg">
                {recommendations.map((rec, idx) => (
                  <p key={idx} className="text-sm">{rec}</p>
                ))}
              </div>
            )}
            <Button variant="outline" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
          </div>
        ) : diagnostic ? (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Empresa */}
              {diagnostic.company && (
                <>
                  <DiagnosticSection icon={Building2} title="Empresa">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">CNPJ:</span>
                      <span className="font-mono">{formatCnpj(diagnostic.company.cnpj)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Razão Social:</span>
                      <span className="font-medium truncate max-w-[200px]" title={diagnostic.company.razao_social}>
                        {diagnostic.company.razao_social || '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Ambiente NF-e:</span>
                      <Badge variant={diagnostic.company.ambiente === 'producao' ? 'default' : 'secondary'}>
                        {ambienteLabel(diagnostic.company.ambiente)}
                      </Badge>
                    </div>
                    {diagnostic.company.nfce_environment && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Ambiente NFC-e:</span>
                        <Badge variant={diagnostic.company.nfce_environment === 'producao' ? 'default' : 'secondary'}>
                          {ambienteLabel(diagnostic.company.nfce_environment)}
                        </Badge>
                      </div>
                    )}
                  </DiagnosticSection>

                  <Separator />
                </>
              )}

              {/* Serviço SEFAZ (status do serviço via motor próprio) */}
              <DiagnosticSection icon={Radio} title="Serviço SEFAZ">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <div className="flex items-center gap-2">
                    <StatusIcon status={getSefazStatus()} />
                    <span className={
                      getSefazStatus() === 'ok' ? 'text-green-600' :
                      getSefazStatus() === 'warning' ? 'text-yellow-600' :
                      'text-red-600'
                    }>
                      {!diagnostic.sefaz
                        ? 'Motor fiscal inacessível'
                        : diagnostic.sefaz.cStat === 107
                          ? 'Em operação'
                          : `${diagnostic.sefaz.cStat ?? '?'}: ${diagnostic.sefaz.xMotivo ?? 'sem retorno'}`}
                    </span>
                  </div>
                </div>
                {diagnostic.sefaz?.dhRecbto && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Consultado em:</span>
                    <span>
                      {format(new Date(diagnostic.sefaz.dhRecbto), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                )}
              </DiagnosticSection>

              {/* Recommendations */}
              {recommendations.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Info className="h-5 w-5 text-blue-500" />
                      <h3 className="font-semibold">Recomendações</h3>
                    </div>
                    <div className="ml-7 space-y-2 p-3 bg-muted/50 rounded-lg">
                      {recommendations.map((rec, idx) => (
                        <p key={idx} className="text-sm">{rec}</p>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        ) : null}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {!isLoading && (
            <Button variant="secondary" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
